import i18n from "i18next";
import { ProjectView } from "src/api/generated/erp/db/types/tables/projectView.ts";
import { getProjectStateLabels } from "src/api/generated/erp/db/types/enums/projectState.ts";
import { ProjectQueryApi } from "src/api/generated/erp/project/project/api/projectQueryApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dateColumn, dateTimeColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ProjectTreeViewContainer } from "../../project/treeView/ProjectTreeViewContainer";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectsOfCustomerDataGridProps {
	customerId: number;
}

export const ProjectsOfCustomerDataGrid = ({ customerId }: ProjectsOfCustomerDataGridProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel
			fetchData={() => ProjectQueryApi.getProjectsConnectedToCustomer({ customerId })}
			getRows={(projects) => projects}
			initialParams={{}}
			getRowId={(row) => row.projectId}
			gridId={"B6F728C1E84643B7"}
			render={({ dataGridProps }) => (
				<AavoDataGrid<ProjectView>
					disableMultipleRowSelection
					columns={[
						integerColumn({
							field: "projectId",
							headerName: i18n.t("number_shortened"),
							width: 80,
						}),
						textColumn({
							field: "siteName",
							headerName: i18n.t("site"),
							width: 90,
						}),
						textColumn({
							field: "projectDescription",
							headerName: i18n.t("name"),
							width: 240,
							mobile: "header",
						}),
						textColumn({
							field: "projectResponsiblePersonName",
							headerName: i18n.t("responsible_person"),
							width: 170,
						}),
						integerColumn({
							field: "customerOrderId",
							headerName: i18n.t("customer_order"),
							width: 130,
							mobile: "cell",
						}),
						dateColumn({
							field: "plannedStartDate",
							headerName: i18n.t("planned_start"),
							width: 140,
						}),
						dateColumn({
							field: "plannedEndDate",
							headerName: i18n.t("planned_end"),
							width: 140,
						}),
						enumColumn({
							field: "projectState",
							headerName: i18n.t("state"),
							enumLabels: getProjectStateLabels(),
							width: 130,
							mobile: "cell",
						}),
						dateTimeColumn({
							field: "createdDate",
							headerName: i18n.t("created_at"),
							width: 170,
						}),
					]}
					openRow={(row) => {
						openDialog({
							title: i18n.t("project"),
							agentContext: {
								key: defineAgentUiContextKey("erp.project.project.projectsOfCustomerDataGrid.dialog"),
								pathSegments: [`${i18n.t("project")} ${row.projectId}`],
								data: {
									customerId,
									projectId: row.projectId,
								},
							},
							content: <ProjectTreeViewContainer onlyProjectId={row.projectId} />,
						});
					}}
					listViewOnMobile
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.customerProjects.grid"),
						data: {
							customerId,
						},
						selectedRowPathSegment: (row) => `${i18n.t("project")} ${row.projectId}`,
						rowFields: ["projectId", "customerOrderId", "projectDescription"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
